<template>
  <v-dialog v-model="dialog" persistent fullscreen transition="dialog-bottom-transition">
    <v-container class="py-0 fill-height" fluid>
      <v-row ref="mapRow" class="fill-height">
        <v-col cols="12" class="px-0 py-0">
          <v-card height="100%">

            <v-toolbar
              color="indigo darken-2"
              dense
              dark>
              <v-toolbar-title>
                <v-icon left>{{ mdiRoad }}</v-icon>

                {{ $vuetify.lang.t('$vuetify.map.travelToolbar.travel') }}
                <span>
                <span v-if="travelData.isLoaded">{{
                    travelData.travel.user.username
                  }} {{
                    $vuetify.lang.t('$vuetify.map.travelToolbar.timePretext')
                  }} {{ travelData.travel.formattedTempTravelStartTime }}</span>
              </span>

              </v-toolbar-title>

              <v-spacer></v-spacer>

              <v-switch v-if="isSpecial"
                        :disabled="!travelData.isLoaded"
                        v-model="travelData.travel.route.isReport"
                        color="primary"
                        class="shrink mt-5 mx-3"
                        readonly
                        @click="tripStateDialog = true">
                <template v-slot:label>
                  {{ $vuetify.lang.t('$vuetify.map.mapDialog.tripReportStatus') }}
                </template>
              </v-switch>

              <template v-if="previousTrip">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn icon @click="changeTripRequest('previous')" v-on="on">
                      <v-icon>
                        {{ previousTripIcon }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $vuetify.lang.t('$vuetify.map.mapDialog.previousTrip') }}</span>
                </v-tooltip>
              </template>

              <template v-if="nextTrip">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn icon @click="changeTripRequest('next')" v-on="on">
                      <v-icon>
                        {{ nextTripIcon }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $vuetify.lang.t('$vuetify.map.mapDialog.nextTrip') }}</span>
                </v-tooltip>
              </template>

              <v-btn icon class="text-right" @click="dialogCloseRequest">
                <v-icon>{{ closeIcon }}</v-icon>
              </v-btn>
            </v-toolbar>

            <v-container fluid class="py-0">
              <v-row>
                <v-col cols="12" md="5" class="pb-0">
                  <v-row no-gutters ref="travelRow">
                    <v-col>
                      <v-skeleton-loader type="card" :loading="!travelData.isLoaded">
                        <travel-stats-card ref="travel-stats-card" :travel="travelData.travel" :raw-values="rawValues"
                                           @route-additional-statistics="applyRouteStatistics"
                                           @feedback-passed="passFeedback"></travel-stats-card>
                      </v-skeleton-loader>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <!-- key is need for unique data for each driver -->
                      <v-scroll-y-transition leave-absolute hide-on-leave>
                        <v-skeleton-loader type="card" :loading="!travelData.isLoaded">
                          <template v-if="!criticalEventView">
                            <trip-scroll :driver-id="travelData.travel.user.userId"
                                         :travel-start="travelData.travel.datetimestart.getTime()"
                                         @trip-request="tripRequest"></trip-scroll>
                          </template>
                          <template v-else>
                            <critical-popup ref="criticalPopup"
                                            :event="events[eventInnerIndex[criticalEventId]]"
                                            :travelId="travelData.travel.travelId"
                                            :route-state="travelData.travel.route.routeState().filter(item => item.hasOwnProperty('style'))"
                                            :user-id="travelData.travel.user.userId"
                                            :raw-values="rawValues" :nano-user="nanoUser"
                                            :inner-index="eventInnerIndex[criticalEventId]"
                                            :previous-event="eventInnerIndex[criticalEventId] !== 0"
                                            :next-event="eventInnerIndex[criticalEventId] !== Object.keys(eventInnerIndex).length - 1"
                                            :is-forced-video="true"
                                            :is-auto-play="autoPlayVideo"
                                            @set-all-camera-correct-events="$emit('set-all-camera-correct-events')"
                                            @set-all-mask-wearing-events="$emit('set-all-mask-wearing-events')"
                                            @critical-event-confirmed="sendState"
                                            @critical-event-change-request="switchCriticalEvent"
                                            @close-critical-event="clearMapMarker"
                                            @critical-mode-changed="setCriticalMode"
                            ></critical-popup>
                          </template>
                        </v-skeleton-loader>
                      </v-scroll-y-transition>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="12" md="7" ref="mapCol" class="px-6 px-md-3">
                  <v-skeleton-loader type="card" :loading="!travelData.isLoaded">
                    <travel-map v-show="criticalMode === criticalPopupMode.MAP" type="local" ref="travelMap"
                                :travel="travelData.travel" :nano-user="nanoUser"
                                :events="events" :grouped-events="groupedEvents"
                                :route-statistics="routeStatistics"
                                :trip-points="tripPoints"
                                :trip-points-loading="tripPointsLoading"
                                :events-local-filter="localEventsFilter"
                                @local-critical-filter-changed="changeLocalCriticalFilter"
                                @critical-event-request="openCriticalEvent"
                                @critical-event-closing="closeCriticalEvent"
                                @critical-event-changed="pushEventChange"
                                @raw-values-changed="setRawValues"
                                @load_trip_points="loadTripPoints"
                    ></travel-map>

                    <critical-dataset-stepper ref="criticalDatasetStepper"
                                              v-show="criticalMode === criticalPopupMode.DATASET"
                                              v-if="events[eventInnerIndex[criticalEventId]] !== undefined"
                                              :dataset-entry="events[eventInnerIndex[criticalEventId]].dataset"
                                              :critical-slides-loaded="criticalSlidesLoading"
                                              :critical-slides-failed="criticalSlidesFailed"
                                              :user="travelData.travel.user.userId"
                                              :trip-start="travelData.travel.datetimestart.getTime()"
                                              :timestamp="events[this.eventInnerIndex[this.criticalEventId]].datetime.getTime()"
                                              @critical-dataset-process-request="sendCriticalDatasetEntry"></critical-dataset-stepper>
                  </v-skeleton-loader>
                </v-col>
              </v-row>
            </v-container>
          </v-card>
        </v-col>
      </v-row>
      <trip-report-state-dialog v-if="travelData.isLoaded"
                                :dialog="tripStateDialog"
                                :is-report="travelData.travel.route.isReport"
                                @report-state-dialog-closed="tripStateDialog = false"
                                @report-state-dialog-reject="tripStateDialog = false"
                                @report-state-dialog-confirm="sendReportState"
      ></trip-report-state-dialog>
    </v-container>
  </v-dialog>
</template>

<script>
import TravelStatsCard from './infoTravelCard/travelStatsCard'
import TripScroll from './infoTravelCard/tripScroll'
import TravelMap from './travelMap'
import { mdiArrowLeftCircle, mdiArrowRightCircle, mdiClose, mdiRoad } from '@mdi/js'
import { AUTH_MODULE, SETTINGS_MODULE } from '@/store/modules'
import { createHelpers } from 'vuex-map-fields'
import { criticalPopupMode, DangerousState, UserCriticalEventConfirmation } from '@/enum'
import CriticalPopup from './criticalPopup/criticalPopup'
import backend from '../../backend'
import CriticalDatasetStepper from './criticalDataset/criticalDatasetStepper'
import snackbarMixin from '../../mixins/snackbarMixin'
import TripReportStateDialog from './tripReportStateDialog'
import { RouteAdditionalStatistics } from '@/models/travel/RouteAdditionalStatistics'
import { PurePoint } from '@/models/travel/PurePoint'
import { CriticalEventLocalFilters, localFiltersFields } from '@/models/map/CriticalEventLocalFilters'

const { mapFields: authFields } = createHelpers({
  getterType: `${AUTH_MODULE}/getField`
})

const { mapFields: settingsFields } = createHelpers({
  getterType: `${SETTINGS_MODULE}/getField`
})

const _getAllowedEvents = (obj, category, events) => {
  const eventField = CriticalEventLocalFilters.criticalEventsMapping()[category]
  const usedValues = Object.keys(obj).filter(key => obj[key] === true)
  const allowedEvents = []

  for (const event of events) {
    // manual category checking
    if (category === localFiltersFields.DANGEROUS_STATE_FILTERS) {
      if (usedValues.includes(event[eventField])) {
        allowedEvents.push(event.idCriticalEvent)
      }
    } else if (category === localFiltersFields.USER_STATE_FILTERS) {
      const isRejectApprovedExists = usedValues.includes(String(UserCriticalEventConfirmation.APPROVED_RETRAIN))
      if (!isRejectApprovedExists) {
        if (usedValues.includes(String(event[eventField])) && !(event[eventField] === UserCriticalEventConfirmation.APPROVE && event.retrainState !== null)) {
          allowedEvents.push(event.idCriticalEvent)
        }
      } else {
        if (event[eventField] === UserCriticalEventConfirmation.APPROVE) {
          if (event.retrainState !== null) {
            allowedEvents.push(event.idCriticalEvent)
          } else if (usedValues.includes(String(UserCriticalEventConfirmation.APPROVE))) {
            allowedEvents.push(event.idCriticalEvent)
          }
        } else if (usedValues.includes(String(event[eventField]))) {
          allowedEvents.push(event.idCriticalEvent)
        }
      }
    }
  }

  return allowedEvents
}

const _getPossibleFilters = (val) => {
  const result = {}
  for (const [field, values] of Object.entries(val)) {
    if (!(field in result)) {
      result[field] = {}
    }

    for (const fieldValue of values) {
      result[field][fieldValue] = true
    }
  }

  return result
}

export default {
  name: 'mapDialog',
  components: { TripReportStateDialog, CriticalDatasetStepper, CriticalPopup, TravelMap, TripScroll, TravelStatsCard },
  props: {
    dialog: Boolean,
    travelData: Object,
    loaded: Boolean,
    nanoUser: Boolean,
    previousTrip: Boolean,
    nextTrip: Boolean
  },
  mixins: [snackbarMixin],
  computed: {
    ...authFields(['isSpecial']),
    ...settingsFields(['mapSettings.hideReviewedEvents', 'mapSettings.hideAllEventsExceptAccepted', 'videoSettings.autoPlayVideo']),
    mapRowHeight () {
      if (!this.$refs.hasOwnProperty('mapRow')) {
        return 0
      } else {
        return this.$refs.mapRow.clientHeight
      }
    },
    events () {
      /* main filter entrypoint! */

      let events = this.travelData.travel.criticalEvents

      // global filters
      if (this.hideReviewedEvents) {
        events = events.filter(event =>
          event.state !== UserCriticalEventConfirmation.REJECT &&
          event.state !== UserCriticalEventConfirmation.APPROVE &&
          event.state !== UserCriticalEventConfirmation.RETRAIN)
      }

      if (this.hideAllEventsExceptAccepted) {
        events = events.filter(event => event.state === UserCriticalEventConfirmation.APPROVE)
      }

      // local filters
      if (Object.keys(this.localEventsFilter).length > 0) {
        const usedCategories = CriticalEventLocalFilters.getFields()
        let allowedEvents = []

        for (const category of usedCategories) {
          const obj = this.localEventsFilter[category]
          allowedEvents.push(new Set([..._getAllowedEvents(obj, category, events)]))
        }

        let eventsIntersection = [...new Set([...allowedEvents[0]].filter(
          x => allowedEvents.slice(1).every(s => s.has(x))
        ))]

        events = events.filter(event => eventsIntersection.includes(event.idCriticalEvent))
      }

      return events
    },
    possibleEventFilters () {
      let events = this.travelData.travel.criticalEvents
      let dangerousStateFilters = new Set()
      let userStateFilters = new Set()

      for (const event of events) {
        dangerousStateFilters.add(event.dangerousStateNormalized)

        let userState = event.state

        if (event.state === UserCriticalEventConfirmation.APPROVE && event.retrainState !== null) {
          userState = UserCriticalEventConfirmation.APPROVED_RETRAIN
        }

        userStateFilters.add(userState)
      }

      return new CriticalEventLocalFilters({ dangerousStateFilters, userStateFilters })
    },
    eventInnerIndex () {
      const eventInnerIndex = {}
      for (let i = 0; i < this.events.length; ++i) {
        eventInnerIndex[this.events[i].idCriticalEvent] = i
      }

      return eventInnerIndex
    },
    groupedEvents () {
      const data = {
        groups: [],
        single: [],
        eventClusters: {} // idCriticalEvent -> group index,
      }

      let index = 0
      const events = this.events
      const length = events.length

      if (length === 0) {
        return data
      } else if (length === 1) {
        data.single.push(events[0])
        return data
      } else {
        const coloredArray = new Array(length)
        const colors = new Array(length).fill(0)
        const groupsColor = [] // {color: index}

        coloredArray[0] = index // first element
        colors[0] = 1

        for (let i = 1; i < length; ++i) {
          const current = events[i]
          const prev = events[i - 1]
          const timeDiff = Math.abs(current.datetime.getTime() - prev.datetime.getTime())

          if (current._dangerousState !== prev._dangerousState || timeDiff > this.clusterEventTimePeriod) {
            index += 1
          }

          coloredArray[i] = index
          colors[index] += 1
        }

        // pass colored array into structure
        for (let i = 0; i < length; ++i) {
          const colorCount = colors[coloredArray[i]]
          if (colorCount === 1) {
            data.single.push(events[i])
          } else if (colorCount > 0) {
            // check if color in group color
            let colorGroup = groupsColor.find(item => item.color === coloredArray[i])
            if (!colorGroup) {
              data.groups.push([])
              groupsColor.push({ color: coloredArray[i], index: data.groups.length - 1 })
              colorGroup = groupsColor.find(item => item.color === coloredArray[i])
            }

            data.eventClusters[events[i].idCriticalEvent] = colorGroup.index
            data.groups[colorGroup.index].push(events[i])
          }
        }

        return data
      }
    }
  },
  data () {
    return {
      criticalEventView: false,
      criticalEventId: null,
      rawValues: false,
      criticalMode: criticalPopupMode.MAP,
      tripStateDialog: false,
      allCameraCorrectDialog: false,
      routeStatistics: new RouteAdditionalStatistics(),
      tripPoints: [],
      tripPointsLoading: false,
      criticalSlidesLoading: false,
      criticalSlidesFailed: false,
      localEventsFilter: {},

      mdiRoad: mdiRoad,
      closeIcon: mdiClose,
      nextTripIcon: mdiArrowRightCircle,
      previousTripIcon: mdiArrowLeftCircle
    }
  },
  methods: {
    openCriticalEvent (criticalEventId) {
      this.criticalEventId = criticalEventId
      this.criticalEventView = true
    },
    async closeCriticalEvent () {
      try {
        const criticalEvent = this.events[this.eventInnerIndex[this.criticalEventId]]
        this.criticalEventId = null

        if (criticalEvent.state === UserCriticalEventConfirmation.UNKNOWN) {
          await this.sendState({
            event: criticalEvent,
            state: UserCriticalEventConfirmation.IGNORE,
            retrainState: criticalEvent.retrainState,
            rejectReason: criticalEvent.rejectReason,
            rejectComment: criticalEvent.rejectComment
          })
        }
      } catch (e) {
        console.error(e)
      }
    },
    async sendState (request) {
      // TODO: convert this to model `CriticalEventConfirmation`
      const data = {
        request: {
          id_critical_event: request.event.idCriticalEvent,
          state: request.state,
          is_demo: request.event.isDemo,
          is_dataset: request.event.isDataset,
          is_turning: request.event.isTurning,
          is_camera_correct: request.event.isCameraCorrect,
          is_look_down: request.event.isLookDown,
          is_wearing_mask: request.event.isWearingMask,
          retrain_state: request.retrainState,
          reject_reason: request.rejectReason,
          reject_comment: request.rejectComment
        },
        index: request.innerIndex
      }

      try {
        const response = await backend.criticalEventConfirmation(data.request)
        if (response.data.success) {
          // TODO: pass id_critical_event among state and search element on this basis
          data.request.demo_changed = request.demoChanged // for frontend because we dont need auto skip events if we change demo switch
          this.$emit('critical-event-changed', data)
          this.$emit('update-report-state') // update trip manager tables with actual data about unreviewed events
        } else {
          console.error(response.data)
        }
      } catch (e) {
        console.error(e)
      }
    },
    switchCriticalEvent ({ direction = '', innerIndex = -1 }) {
      if (innerIndex === -1 || direction === '') {
        return
      }

      const delta = direction === 'next' ? 1 : -1

      if (innerIndex + delta >= 0 && innerIndex + delta <= this.events.length - 1) {
        const event = this.events[innerIndex + delta]
        this.$refs['travelMap'].openCriticalEventManually(event.idCriticalEvent)
        this.resetCriticalModeGlobally()
      }
    },
    async sendCriticalDatasetEntry () {
      const data = {
        critical_dataset: this.events[this.eventInnerIndex[this.criticalEventId]].dataset.backendModel(),
        critical_event: this.criticalEventId
      }

      try {
        await backend.processCriticalDatasetEntry(data)
        this.openSuccessSnackbar(`$vuetify.criticalDataset.popup.success`)
        this.events[this.eventInnerIndex[this.criticalEventId]].dataset.isEmpty = false
      } catch (e) {
        console.error(e)
        this.openFailureSnackbar(`$vuetify.criticalDataset.popup.failure`)
      } finally {
        this.resetCriticalModeGlobally()
      }
    },
    resetRouteAdditionalStatistics () {
      this.routeStatistics = new RouteAdditionalStatistics()
    },
    resetCriticalModeGlobally () {
      this.criticalMode = criticalPopupMode.MAP
      this.$refs['criticalPopup'].resetCriticalMode()
    },
    changeTripRequest (direction) {
      this.$emit('change-trip-request', direction)
      this.criticalMode = criticalPopupMode.MAP
      this.resetRouteAdditionalStatistics()
      this.$refs['criticalPopup'].resetCriticalMode()
    },
    clearMapMarker () {
      this.$refs['travelMap'].clearEvent()
      this.criticalEventView = false
    },
    setRawValues (value) {
      this.rawValues = value
    },
    passFeedback (feedback) {
      this.$emit('feedback-passed', feedback)
    },
    tripRequest (request) {
      this.$emit('trip-request', request)
    },
    dialogCloseRequest () {
      this.clearMapMarker()
      this.resetRouteAdditionalStatistics()
      this.$emit('close-map-dialog')
    },
    pushEventChange (data) {
      this.$emit('critical-event-changed', data)
    },
    keyDown (data) {
      if (data.keyCode === 27) {
        this.$emit('close-map-dialog')
      }
    },
    async setCriticalMode (value) {
      this.criticalMode = value

      // check if we dataset now...
      if (value === criticalPopupMode.DATASET) {
        this.criticalSlidesLoading = true
        this.criticalSlidesFailed = false

        try {
          // loading slides flag here...
          await backend.checkCriticalSlides({
            user: this.travelData.travel.user.userId,
            trip_start: this.travelData.travel.datetimestart.getTime(),
            timestamp: this.events[this.eventInnerIndex[this.criticalEventId]].datetime.getTime()
          })
        } catch (e) {
          console.error(e)
          this.criticalSlidesFailed = true
        } finally {
          this.criticalSlidesLoading = false
        }
      }
    },
    async sendReportState () {
      this.travelData.travel.route.isReport = !this.travelData.travel.route.isReport

      try {
        await backend.setTripReportStatus({
          trip_id: this.travelData.travel.travelId,
          is_report: this.travelData.travel.route.isReport
        })
        this.$emit('update-report-state')
      } catch (e) {
        console.error(e)
        this.openFailureSnackbar(`$vuetify.map.mapDialog.tripReportStatusFailure`)
      }
    },
    applyRouteStatistics (route) {
      this.routeStatistics = new RouteAdditionalStatistics({ ...route })
    },
    async loadTripPoints () {
      this.tripPointsLoading = true

      try {
        let data = await backend.getTripRoadPoints({
          'user_id': this.travelData.travel.user.userId,
          'datetimestart': this.travelData.travel.datetimestart.getTime()
        })

        this.tripPoints = data.data.map(item => new PurePoint({ ...item }))
      } catch (e) {
        console.error(e)
        this.openFailureSnackbar(e)
      } finally {
        this.tripPointsLoading = false
      }
    },
    changeLocalCriticalFilter (entry) {
      this.localEventsFilter[entry.category][entry.field] = entry.value
    },
    clearLocalCriticalFilter () {
      this.localEventsFilter = {}
    }
  },
  created () {
    this['criticalPopupMode'] = criticalPopupMode
  },
  watch: {
    loaded: {
      // immediate: true,
      handler (value) {
        this.$nextTick(async () => {
          this.tripPoints = [] // reset loaded pure points

          if (this.$refs.hasOwnProperty('travel-stats-card')) {
            await this.$refs['travel-stats-card'].loadAdditionalStatistics()
          }
          if (this.$refs.hasOwnProperty('criticalPopup')) {
            await this.$refs['criticalPopup'].getFilteredEventValues()
          }

        })
      }
    },
    possibleEventFilters (newVal) {
      let possibleFilters = _getPossibleFilters(newVal)
      if (Object.keys((this.localEventsFilter)).length === 0) {
        // trip loading
        this.localEventsFilter = Object.assign({}, possibleFilters)
      } else {
        // add new critical events states to the current with merge
        for (const [field, values] of Object.entries(possibleFilters)) {
          for (const value of Object.keys(values)) {
            if (!(value in this.localEventsFilter[field])) {
              this.$set(this.localEventsFilter[field], value, true)
            }
          }
        }
      }
    }
  }
}
</script>

<style scoped>
>>> .v-dialog {
  box-shadow: none;
}
</style>
